<template>
  <div class="training-form view">
    <form v-if="entity" class="row" @submit.prevent="onSave({close: false})">
      <div class="col">
        <form-model-controls :docs="actionDocs" v-model="entity"/>

      </div>

      <div class="col-5">

        <label>{{ $t('speakers') }}</label>
        <sortable-list :items="entity.speakers">
          <template v-slot:header="{toggleDirection, setNumericSort,setSortKey, sortKey, sortDirection}">
            <div class="row">
              <sortable-list-column-header
                  class="col-6"
                  sort-key="name"
                  :sort-a-s-c="sortDirection"
                  :sort-by="sortKey"
                  :set-sort-key="setSortKey"
                  :toggle-direction="toggleDirection"
              />
              <sortable-list-column-header
                  class="col-4"
                  sort-key="company"
                  :sort-a-s-c="sortDirection"
                  :sort-by="sortKey"
                  :set-sort-key="setSortKey"
                  :toggle-direction="toggleDirection"
              />
            </div>
          </template>
          <template v-slot:itemTemplate="{item, itemIndex}">
            <div class="row align-items-center">
              <div class="col-6">
                {{ item.name }}
              </div>
              <div class="col-4">
                {{ item.duration }}
              </div>
              <div class="col-2 pr-0">
                <button type="button"
                        class="btn btn-danger"
                        @click="entity.speakers.splice(itemIndex,1)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </template>
          <template v-slot:emptyList>
            <empty-list-note
                :body-text="$t('Dieser Termin hat noch keine Referenten')"
            />
          </template>
        </sortable-list>
        <div class="d-flex justify-content-center">
          <form-select class="flex-grow-1"
                       :options="filteredSpeakers"
                       option-label-key="name"
                       v-model="selectedSpeaker"/>
          <button type="button"
                  class="btn btn-primary"
                  @click="addSpeaker"
          >{{ $t('add speaker') }}
          </button>
        </div>
      </div>
      <div class="col-12 py-5">

        <bottom-tool-bar :can-delete="!!trainingDateId" @save="onSave" @delete="onDelete"/>
      </div>
      <confirmation-modal v-if="showDirtyModal"
                          :title="$t('Achtung')"
                          @close="cancelNavigation">
        <template v-slot:default>
          {{ $t('Sie haben ungespeicherte Änderungen. Wie wollen Sie fortfahren?') }}
        </template>
        <template v-slot:footer>
          <button type="button" class="btn btn-danger" @click="dontSave">
            {{ $t('Nicht speichern') }}
          </button>
          <button type="button" class="btn btn-primary" @click="onSave({close:true})">
            {{ $t('Speichern') }}
          </button>
        </template>
      </confirmation-modal>
    </form>
    <div v-else class="text-center">
      <img src="/img/loading.svg" alt="loading">
    </div>
  </div>
</template>

<script>
import {mapActions, mapState} from "vuex";
import dayjs from "dayjs";
// Mixins
import ViewDirtyMixin from '@/mixins/view-dirty'
// Components
import FormInput from "@/components/Form/FormInput";
import FormTextarea from "@/components/Form/FormTextarea";
import SortableList from "@/components/SortableList";
import EmptyListNote from "@/components/EmptyListNote";
import SortableListColumnHeader from "@/components/SortableListColumnHeader";
import FormSelect from "@/components/Form/FormSelect";
import FormModelControls from "@/components/Form/FormModelControls";
import BottomToolBar from "@/components/BottomToolBar";
import ConfirmationModal from "@/components/ConfirmationModal";

export default {
  name: "TrainingDateForm",
  mixins: [ViewDirtyMixin],
  components: {
    ConfirmationModal,
    BottomToolBar,
    FormModelControls,
    FormSelect, SortableListColumnHeader, EmptyListNote, SortableList, FormTextarea, FormInput
  },
  props: {
    regionId: {type: String, default: null},
    trainingId: {type: String, default: null},
    trainingDateId: {type: String, default: null},
  },
  data() {
    return {
      entity: null,
      selectedSpeaker: null
    }
  },
  computed: {
    ...mapState({
      speakers: state => state.Api.Speakers.all
    }),
    actionDocs() {
      if (this.trainingDateId) {
        return this.$apiData.TrainingDates.find(action => action.name === 'modify')
      }

      return this.$apiData.TrainingDates.find(action => action.name === 'add')
    },
    filteredSpeakers() {
      return this.speakers
          .filter(el => el.region_id === this.regionId)
          .filter(el => !this.entity.speakers.find(f => f.id === el.id))
    }
  },
  created() {
    this.getSpeakers({region_id: this.regionId, limit: Number.MAX_SAFE_INTEGER})
        .catch(this.apiErrorHandler)

    if (this.trainingDateId) {
      this.getTrainingDate({id: this.trainingDateId})
          .then(training => this.entity = training)
          .catch(this.apiErrorHandler);

    } else {
      this.entity = {
        training_id: this.trainingId,
        date: '',
        duration: '',
        location: '',
        fee: '',
        registration_link: '',
        speakers: [],
      }
    }
  },
  methods: {
    ...mapActions({
      addTrainingDate: "Api/TrainingDates/add",
      getTrainingDate: "Api/TrainingDates/get",
      deleteTrainingDate: "Api/TrainingDates/delete",
      modifyTrainingDate: "Api/TrainingDates/modify",
      getSpeakers: "Api/Speakers/all"
    }),
    addSpeaker() {
      let speaker = this.filteredSpeakers.slice().find(el => el.id === this.selectedSpeaker);
      this.entity.speakers.push(speaker);
      this.selectedSpeaker = null;
    },
    onSave({close}) {
      if (this.trainingDateId) {
        this.modifyTrainingDate(this.entity)
            .then(close ? this.saveComplete : this.saveCompleteToast)
            .catch(this.apiErrorHandler);
      } else {
        this.addTrainingDate({
          ...this.entity,
          training_id: this.trainingId,
          date: dayjs(this.entity.date).format(this.$config.API_DATE_FORMAT)
        })
            .then(close ? this.saveComplete : entity => {
              this.entity = entity;
              this.saveCompleteToast();
              this.$router.replace(['/regions', this.regionId, 'trainings', this.trainingId, 'training-date/edit', entity.id].join('/'))
            })
            .catch(this.apiErrorHandler);
      }
    },
    onDelete() {
      this.deleteTrainingDate({id: this.trainingDateId})
          .then(this.saveComplete)
          .catch(this.apiErrorHandler);
    }
  }
}
</script>
